'use client'
import { CalendarIcon } from '../icons'
import React, { useRef, useState } from 'react'
import { DateValue, TimeValue, useButton, useDatePicker, useInteractOutside } from 'react-aria'
import { DatePickerStateOptions, useDatePickerState } from 'react-stately'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'
import { Button } from '../button'
import { Calendar } from './calendar'
import { TimeField } from './time-field'
import { getLocalTimeZone, parseAbsolute } from '@internationalized/date'
import { format } from 'date-fns'
import { cva } from 'class-variance-authority'

/* 
This component supports a shadcn integration for a date-time-picker and was written by @uncvrd https://github.com/uncvrd/shadcn-ui-date-time-picker
Original issue: https://github.com/shadcn-ui/ui/issues/255
TODO: replace this component with an official shadcn datepicker after this issue is closed & supported by the library
*/

const formatDateForPicker = (timestamp: string) => {
  if (!timestamp) return null
  const date = new Date(Number(timestamp) * 1000)
  return parseAbsolute(date.toISOString(), getLocalTimeZone())
}

const convertDateValueToString = (date: DateValue) => {
  const timestamp = Math.floor(date.toDate(getLocalTimeZone()).getTime() / 1000).toString()
  return timestamp
}

const dateTimePickerVariants = cva(
  'ring-offset-background focus-within:ring-ring flex items-center rounded-md focus-within:ring-2 focus-within:ring-offset-2',
  {
    variants: {
      variant: {
        default: '',
        inverted: 'bg-surface-inverted text-on-surface-inverted'
      },
      buttonVariant: {
        default:
          'text-on-primary border-outline-variant leading-xs focus-visible:ring-primary ring-offset-background py-xs px-sm inline-flex h-10 items-center justify-center space-x-1 rounded rounded-l-none border bg-white text-sm transition-colors hover:bg-white focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        inverted:
          '!bg-surface-inverted text-on-primary border-outline-inverted leading-xs focus-visible:ring-primary ring-offset-background py-xs px-sm inline-flex h-10 items-center justify-center space-x-1 rounded rounded-l-none border text-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50'
      },
      calendarVariant: {
        default: 'bg-white text-on-surface',
        inverted:
          'bg-surface-inverted text-on-surface-inverted rounded-xs border-outline-inverted text-on-surface-inverted'
      },
      iconVariant: {
        default: 'fill-on-surface',
        inverted: 'bg-surface-inverted fill-primary-inverted'
      },
      inputVariant: {
        default:
          'border-outline-variant ring-offset-background focus-visible:ring-ring inline-flex h-10 flex-1 items-center rounded-l-md border border-r-0 bg-transparent py-2 pl-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        inverted:
          'bg-surface-inverted text-on-surface-inverted border-outline-inverted rounded-l-xs rounded-r-none !border-r-0 ring-offset-background focus-visible:ring-ring inline-flex h-10 flex-1 items-center border py-2 pl-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
      }
    },
    defaultVariants: {
      variant: 'default',
      calendarVariant: 'default',
      iconVariant: 'default'
    }
  }
)

interface DateTimePickerProps extends DatePickerStateOptions<DateValue> {
  onChange: (date: DateValue | null) => void
  className?: string
  id?: string
  variant?: 'default' | 'inverted'
}

const DateTimePicker = React.forwardRef<HTMLDivElement, DateTimePickerProps>((props, forwardedRef) => {
  const ref = useForwardedRef(forwardedRef)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  const state = useDatePickerState(props)

  function useForwardedRef<T>(ref: React.ForwardedRef<T>): React.RefObject<T> {
    const innerRef = React.useRef<T>(null)

    React.useEffect(() => {
      if (!ref) return
      if (typeof ref === 'function') {
        ref(innerRef.current)
      } else {
        ref.current = innerRef.current
      }
    })

    return innerRef
  }

  const {
    groupProps,
    fieldProps,
    buttonProps: _buttonProps,
    dialogProps,
    calendarProps
  } = useDatePicker(props, state, ref)
  const { buttonProps } = useButton(_buttonProps, buttonRef)
  useInteractOutside({
    ref: contentRef,
    onInteractOutside: () => {
      setOpen(false)
    }
  })

  const date = fieldProps.value ? format(fieldProps.value?.toDate(getLocalTimeZone()), 'PPp') : null
  return (
    <div
      {...groupProps}
      ref={ref}
      className={cn(groupProps.className, dateTimePickerVariants({ variant: props.variant }), props.className)}
    >
      <div
        ref={ref}
        className={cn(
          dateTimePickerVariants({ inputVariant: props.variant }),
          props.isDisabled ? 'cursor-not-allowed opacity-50' : ''
        )}
      >
        {date}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            {...buttonProps}
            className={cn(dateTimePickerVariants({ buttonVariant: props.variant }))}
            disabled={props.isDisabled}
            onClick={() => setOpen(true)}
          >
            <CalendarIcon className={cn('h-5 w-5', dateTimePickerVariants({ iconVariant: props.variant }))} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          ref={contentRef}
          className={cn(
            'border-outline-variant w-full shadow-[2px_2px_6px_0px_rgba(0,0,0,0.20)]',
            dateTimePickerVariants({ calendarVariant: props.variant })
          )}
        >
          <div
            {...dialogProps}
            className={cn('flex flex-col space-y-3', dateTimePickerVariants({ calendarVariant: props.variant }))}
          >
            <Calendar {...calendarProps} variant={props.variant} />
            {!!state.hasTime && (
              <TimeField
                value={state.timeValue}
                onChange={(value) => state.setTimeValue(value as TimeValue)}
                aria-label="Set a time"
                variant={props.variant}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
})

DateTimePicker.displayName = 'DateTimePicker'

export { DateTimePicker, formatDateForPicker, convertDateValueToString, type DateValue }
